<template>
	<Modal
		v-model:visible="visible"
		:title="$t('settings.profileManagement.accessibleBaseStationsModal.header')"
		@ok="onOk"
		@cancel="onCancel"
	>
		<Input
			v-model:value="searchQuery"
			class="width-100"
			allowClear
			:placeholder="$t('settings.profileManagement.accessibleBaseStationsModal.searchPlaceholder')"
		/>
		<Space class="pd-vertical">
			<Button size="small" @click="onSelectAll">
				{{ $t('settings.profileManagement.accessibleBaseStationsModal.selectAll') }}
			</Button>
			<Button size="small" @click="onDeselectAll">
				{{ $t('settings.profileManagement.accessibleBaseStationsModal.deselectAll') }}
			</Button>
		</Space>
		<CheckboxesComponent v-model:value="selectedIds" bordered class="accessible-stations-checkboxes">
			<Checkbox
				v-for="baseStation in filteredStations"
				:key="baseStation.id"
				:value="baseStation.id"
				class="nobr"
			>
				<TypographyText strong>{{ baseStation.mntpnt_int }}</TypographyText> | {{ baseStation.label }}
			</Checkbox>
		</CheckboxesComponent>
		<template #footer>
			<Button key="back" @click="onCancel">
				{{ $t('settings.profileManagement.accessibleBaseStationsModal.cancelButtonLabel') }}
			</Button>
			<Button key="submit" type="primary" :loading="isSubmitting" @click="onOk">
				{{ $t('settings.profileManagement.accessibleBaseStationsModal.okButtonLabel') }}
			</Button>
		</template>
	</Modal>
</template>

<script lang="ts">
import { computed, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';
import { Button, Checkbox, Input, Modal, Space, TypographyText, message } from 'ant-design-vue';
import type { PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import Utils from '@/api/Utils';
import CheckboxesComponent from '@/components/common/CheckboxesComponent.vue';
import { useProfileUpdate } from '@/components/settings/profiles/useProfileUpdate';
import {
	filterAccessibleStations,
	type AccessibleStationOption,
} from '@/components/settings/profiles/accessibleBaseStationsModalUtils';


type AccessibleProfile = {
	username: string;
	accessibleBaseStations?: number[];
};


export default {
	components: {
		Modal, Input, Space, Button, Checkbox, TypographyText,
		CheckboxesComponent,
	},
	props: {
		modelValue: {
			type: Boolean,
			required: true,
		},
		profile: {
			type: Object as PropType<AccessibleProfile | null>,
			default: null,
		},
		stations: {
			type: Array as PropType<AccessibleStationOption[]>,
			default: () => [],
		},
		onProfileUpdate: {
			type: Function as PropType<() => void>,
			required: true,
		},
	},
	emits: ['update:modelValue'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const visible = useVModel(props, 'modelValue', emit);
		const searchQuery = ref('');
		const selectedIds = ref<number[]>([]);
		const isSubmitting = ref(false);

		const stations = computed((): AccessibleStationOption[] => Utils.sortArray(
			props.stations.filter(baseStation => Boolean(baseStation.mntpnt_int)),
			Utils.objectFieldMultiSorter('mntpnt_int'),
		));

		const filteredStations = computed(() => filterAccessibleStations(stations.value, searchQuery.value));

		watch(visible, value => {
			if (!value || props.profile == null) {
				return;
			}
			searchQuery.value = '';
			selectedIds.value = [...(props.profile.accessibleBaseStations ?? [])];
			isSubmitting.value = false;
		});

		const { profileUpdate } = useProfileUpdate(
			() => { },
			() => {
				isSubmitting.value = false;
				message.success(t('settings.profileManagement.accessibleBaseStationsModal.messages.success'));
				visible.value = false;
				props.onProfileUpdate();
			},
			() => {
				isSubmitting.value = false;
			},
		);

		const onSelectAll = () => {
			const filteredIds = new Set(filteredStations.value.map(station => station.id));
			const keptIds = selectedIds.value.filter(id => !filteredIds.has(id));
			selectedIds.value = [...keptIds, ...filteredStations.value.map(station => station.id)];
		};

		const onDeselectAll = () => {
			const filteredIds = new Set(filteredStations.value.map(station => station.id));
			selectedIds.value = selectedIds.value.filter(id => !filteredIds.has(id));
		};

		const onOk = () => {
			if (props.profile == null) {
				return;
			}
			isSubmitting.value = true;
			profileUpdate(props.profile, {
				accessible_base_stations: selectedIds.value,
			});
		};

		const onCancel = () => {
			visible.value = false;
		};

		return {
			visible,
			searchQuery,
			selectedIds,
			filteredStations,
			isSubmitting,
			onSelectAll,
			onDeselectAll,
			onOk,
			onCancel,
		};
	},
};
</script>

<style scoped>
.accessible-stations-checkboxes {
	max-height: 300px;
	overflow: auto;
	display: flex;
	flex-direction: column;
}
</style>
